import axios from 'axios';

// Get auth token from environment
const AUTH_TOKEN = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Create axios instance for edge functions
// We'll use a proxy in development to avoid CORS
const edgeFunctionAxios = axios.create({
    baseURL: '/supabase-functions',
    headers: {
        'Authorization': `Bearer ${AUTH_TOKEN}`,
        'Content-Type': 'application/json',
    },
});

// Add response interceptor for error handling
edgeFunctionAxios.interceptors.response.use(
    (response) => response,
    (error) => {
        return Promise.reject(error);
    }
);

// Generic edge function caller
export const callEdgeFunction = async <T = any>(
    functionName: string,
    body?: any
): Promise<T> => {
    const response = await edgeFunctionAxios.post<T>(`/${functionName}`, body);
    return response.data;
};

// Specific function for parties report
export interface PartiesReportParams {
    party_id?: number;
    site_ids?: number[];
    party_ids?: number[];
    party_group_ids?: number[];
    responsible_party_ids?: number[];
}

export interface PartyReportItem {
    party_id: number;
    party_name: string;
    sales_invoices_count: number;
    receipts_count: number;
    closing_balance: number;
    closing_balance_type: 'Dr' | 'Cr';
    unallocated_credit: number;
}

export const fetchPartiesReport = async (params: PartiesReportParams = {}): Promise<PartyReportItem[]> => {
    // Get organization ID from auth store
    const { userOrgId } = await import('@/app/store/authStore').then(m => m.useAuthStore.getState());

    if (!userOrgId) {
        throw new Error('No organization selected. Please ensure you are logged in.');
    }

    // Convert arrays to comma-separated strings for query parameters (same as sales-report)
    const queryParams = new URLSearchParams();
    if (params.site_ids?.length) queryParams.append('site_ids', params.site_ids.join(','));
    if (params.party_ids?.length) queryParams.append('party_ids', params.party_ids.join(','));
    if (params.party_group_ids?.length) queryParams.append('party_group_ids', params.party_group_ids.join(','));
    if (params.responsible_party_ids?.length) queryParams.append('responsible_party_ids', params.responsible_party_ids.join(','));

    const response = await edgeFunctionAxios.get<{ data: PartyReportItem[] }>(`/party-statement?${queryParams.toString()}`, {
        headers: {
            'X-Organization-ID': userOrgId.toString()
        }
    });

    // Sort by closing balance (highest to lowest)
    const sortedData = response.data.data.sort((a, b) => b.closing_balance - a.closing_balance);

    return sortedData;
};

// Deprecated: Use fetchSalesReportData instead
export const fetchSalesReport = async (params: { start_date: string; end_date: string; party_id?: number }) => {
    return callEdgeFunction('sales-report', params);
};

export interface InventoryReportParams {
    item_id?: number;
    godown_id?: number;
    as_of_date?: string;
}

export const fetchInventoryReport = async (params: InventoryReportParams) => {
    return callEdgeFunction('inventory-report', params);
};

// Delivery Metrics Types and Functions
export interface DeliveryMetricsParams {
    start_date: string;
    end_date: string;
    period?: 'daily' | 'weekly' | 'monthly';
    site_id?: number;
}

export interface DeliveryMetricsItem {
    period_start: string;
    period_label: string;
    total_invoices: number;
    invoices_packed: number;
    invoices_shipped: number;
    invoices_delivered: number;
    invoices_pending: number;
    packing_rate: number;
    shipping_rate: number;
    delivery_rate: number;
    avg_invoice_to_delivery_hours: number;
    total_invoice_value: number;
    delivered_value: number;
}

export interface DeliveryJourneyItem {
    stage: string;
    count: number;
    percentage: number;
    avg_time_to_stage_hours: number | null;
    total_value: number;
}

export interface StagePerformanceItem {
    period_start: string;
    period_label: string;
    invoice_to_packed_hours: number;
    packed_to_shipped_hours: number;
    shipped_to_delivered_hours: number;
    total_cycle_time_hours: number;
    efficiency_score: number;
}

export interface ChallanMetrics {
    total_challans: number;
    challans_packed: number;
    challans_shipped: number;
    challans_delivered: number;
    packing_rate: number;
    shipping_rate: number;
    delivery_completion_rate: number;
    invoices_with_challans: number;
    invoices_without_challans: number;
}

// Fetch delivery metrics
export const fetchDeliveryMetrics = async (params: DeliveryMetricsParams): Promise<DeliveryMetricsItem[]> => {
    const { userOrgId } = await import('@/app/store/authStore').then(m => m.useAuthStore.getState());
    
    if (!userOrgId) {
        throw new Error('No organization selected. Please ensure you are logged in.');
    }
    
    const response = await edgeFunctionAxios.get<{ data: DeliveryMetricsItem[] }>('/delivery-metrics/metrics', {
        params,
        headers: {
            'X-Organization-ID': userOrgId.toString()
        }
    });
    
    return response.data.data;
};

// Fetch delivery journey
export const fetchDeliveryJourney = async (params: Omit<DeliveryMetricsParams, 'period'>): Promise<DeliveryJourneyItem[]> => {
    const { userOrgId } = await import('@/app/store/authStore').then(m => m.useAuthStore.getState());
    
    if (!userOrgId) {
        throw new Error('No organization selected. Please ensure you are logged in.');
    }
    
    const response = await edgeFunctionAxios.get<{ data: DeliveryJourneyItem[] }>('/delivery-metrics/journey', {
        params,
        headers: {
            'X-Organization-ID': userOrgId.toString()
        }
    });
    
    return response.data.data;
};

// Fetch stage performance
export const fetchStagePerformance = async (params: DeliveryMetricsParams): Promise<StagePerformanceItem[]> => {
    const { userOrgId } = await import('@/app/store/authStore').then(m => m.useAuthStore.getState());
    
    if (!userOrgId) {
        throw new Error('No organization selected. Please ensure you are logged in.');
    }
    
    const response = await edgeFunctionAxios.get<{ data: StagePerformanceItem[] }>('/delivery-metrics/stage-performance', {
        params,
        headers: {
            'X-Organization-ID': userOrgId.toString()
        }
    });
    
    return response.data.data;
};

// Fetch challan metrics
export const fetchChallanMetrics = async (params: Omit<DeliveryMetricsParams, 'period'>): Promise<ChallanMetrics> => {
    const { userOrgId } = await import('@/app/store/authStore').then(m => m.useAuthStore.getState());
    
    if (!userOrgId) {
        throw new Error('No organization selected. Please ensure you are logged in.');
    }
    
    const response = await edgeFunctionAxios.get<{ data: ChallanMetrics }>('/delivery-metrics/challan-metrics', {
        params,
        headers: {
            'X-Organization-ID': userOrgId.toString()
        }
    });
    
    return response.data.data;
};

// Customer Metrics Types and Functions
export interface CustomerLifetimeMetric {
    party_id: number;
    party_name: string;
    party_code: string;
    first_order_date: string;
    last_order_date: string;
    total_orders: number;
    total_spent: number;
    avg_order_value: number;
    order_frequency_days: number | null;
    days_since_last_order: number;
    customer_status: 'Active' | 'At Risk' | 'Dormant' | 'Lost';
    outstanding_amount: number;
}

export interface RetentionMetric {
    period_start: string;
    period_label: string;
    total_customers: number;
    new_customers: number;
    returning_customers: number;
    lost_customers: number;
    retention_rate: number;
    churn_rate: number;
    avg_customer_value: number;
}

export interface CreditAnalysis {
    credit_range: string;
    customer_count: number;
    total_outstanding: number;
    avg_outstanding: number;
    percentage_of_customers: number;
    percentage_of_outstanding: number;
}

export interface OrderPattern {
    day_of_week: number;
    day_name: string;
    order_count: number;
    unique_customers: number;
    total_value: number;
    avg_order_value: number;
    percentage_of_orders: number;
}

export interface ProductPreference {
    item_id: number;
    item_name: string;
    item_code: string;
    category_name: string | null;
    total_quantity: number;
    order_count: number;
    total_value: number;
    avg_quantity_per_order: number;
    last_ordered: string;
}

export interface CustomerMetricsParams {
    start_date: string;
    end_date: string;
    site_id?: number;
    limit?: number;
}

export interface RetentionParams extends CustomerMetricsParams {
    period?: 'monthly' | 'quarterly' | 'yearly';
}

export interface CreditAnalysisParams {
    as_of_date?: string;
    site_id?: number;
}

export interface ProductPreferenceParams {
    party_id: number;
    start_date: string;
    end_date: string;
    limit?: number;
}

// Fetch customer lifetime metrics
export const fetchCustomerLifetimeMetrics = async (params: CustomerMetricsParams): Promise<CustomerLifetimeMetric[]> => {
    const { userOrgId } = await import('@/app/store/authStore').then(m => m.useAuthStore.getState());
    
    if (!userOrgId) {
        throw new Error('No organization selected. Please ensure you are logged in.');
    }
    
    const response = await edgeFunctionAxios.get<{ data: CustomerLifetimeMetric[] }>('/customer-metrics/lifetime-metrics', {
        params,
        headers: {
            'X-Organization-ID': userOrgId.toString()
        }
    });
    
    return response.data.data;
};

// Fetch customer retention metrics
export const fetchCustomerRetention = async (params: RetentionParams): Promise<RetentionMetric[]> => {
    const { userOrgId } = await import('@/app/store/authStore').then(m => m.useAuthStore.getState());
    
    if (!userOrgId) {
        throw new Error('No organization selected. Please ensure you are logged in.');
    }
    
    const response = await edgeFunctionAxios.get<{ data: RetentionMetric[] }>('/customer-metrics/retention', {
        params,
        headers: {
            'X-Organization-ID': userOrgId.toString()
        }
    });
    
    return response.data.data;
};

// Fetch credit analysis
export const fetchCreditAnalysis = async (params: CreditAnalysisParams = {}): Promise<CreditAnalysis[]> => {
    const { userOrgId } = await import('@/app/store/authStore').then(m => m.useAuthStore.getState());
    
    if (!userOrgId) {
        throw new Error('No organization selected. Please ensure you are logged in.');
    }
    
    const response = await edgeFunctionAxios.get<{ data: CreditAnalysis[] }>('/customer-metrics/credit-analysis', {
        params,
        headers: {
            'X-Organization-ID': userOrgId.toString()
        }
    });
    
    return response.data.data;
};

// Fetch order patterns
export const fetchOrderPatterns = async (params: CustomerMetricsParams): Promise<OrderPattern[]> => {
    const { userOrgId } = await import('@/app/store/authStore').then(m => m.useAuthStore.getState());
    
    if (!userOrgId) {
        throw new Error('No organization selected. Please ensure you are logged in.');
    }
    
    const response = await edgeFunctionAxios.get<{ data: OrderPattern[] }>('/customer-metrics/order-patterns', {
        params,
        headers: {
            'X-Organization-ID': userOrgId.toString()
        }
    });
    
    return response.data.data;
};

// Fetch product preferences
export const fetchProductPreferences = async (params: ProductPreferenceParams): Promise<ProductPreference[]> => {
    const { userOrgId } = await import('@/app/store/authStore').then(m => m.useAuthStore.getState());

    if (!userOrgId) {
        throw new Error('No organization selected. Please ensure you are logged in.');
    }

    const response = await edgeFunctionAxios.get<{ data: ProductPreference[] }>('/customer-metrics/product-preferences', {
        params,
        headers: {
            'X-Organization-ID': userOrgId.toString()
        }
    });

    return response.data.data;
};

// Sales Report Types and Functions
export interface SalesReportParams {
    start_date?: string;
    end_date?: string;
    site_ids?: number[];
    party_ids?: number[];
    party_group_ids?: number[];
    responsible_party_ids?: number[];
    aggregation_level?: 'detail' | 'party' | 'site' | 'party_group' | 'overall';
    summary_only?: boolean;
}

export interface SalesReportRow {
    party_id: number | null;
    party_name: string | null;
    party_group_id: number | null;
    party_group_name: string | null;
    site_id: number | null;
    site_name: string | null;
    invoice_count: number;
    cancelled_invoice_count: number;
    total_quantity_sold: number;
    unique_items_count: number;
    unique_categories_count: number;
    gross_sales: number;          // Pre-discount, pre-tax value
    discount_amount: number;       // Total discounts given
    net_sales: number;            // After discount, before tax
    taxable_amount: number;       // Amount on which tax is calculated
    tax_amount: number;           // Total tax
    total_invoice_value: number;  // Final invoice amount
    avg_order_value: number;
    avg_items_per_invoice: number;
    avg_discount_percentage: number;
}

export interface SalesReportSummary {
    period_start: string;
    period_end: string;
    total_parties: number;
    total_invoices: number;
    gross_sales: number;
    total_discount_given: number;
    net_sales: number;
    total_tax: number;
    total_invoice_value: number;
    avg_invoice_value: number;
    avg_discount_rate: number;
}

export interface SalesReportResponse {
    data?: SalesReportRow[];
    summary?: SalesReportSummary[];
    metadata?: {
        total_rows: number;
        aggregation_level: string;
        summary: {
            total_gross_sales: number;
            total_net_sales: number;
            total_invoice_value: number;
            total_invoices: number;
            average_invoice_value: number;
        };
    };
    parameters: {
        organization_id: string;
        start_date: string;
        end_date: string;
        aggregation_level?: string;
        filters_applied: {
            site_ids: string | null;
            party_ids: string | null;
            party_group_ids: string | null;
        };
    };
}

// Fetch sales report data
export const fetchSalesReportData = async (params: SalesReportParams): Promise<SalesReportResponse> => {
    const { userOrgId } = await import('@/app/store/authStore').then(m => m.useAuthStore.getState());

    if (!userOrgId) {
        throw new Error('No organization selected. Please ensure you are logged in.');
    }

    // Convert arrays to comma-separated strings for the API
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.site_ids?.length) queryParams.append('site_ids', params.site_ids.join(','));
    if (params.party_ids?.length) queryParams.append('party_ids', params.party_ids.join(','));
    if (params.party_group_ids?.length) queryParams.append('party_group_ids', params.party_group_ids.join(','));
    if (params.responsible_party_ids?.length) queryParams.append('responsible_party_ids', params.responsible_party_ids.join(','));
    if (params.aggregation_level) queryParams.append('aggregation_level', params.aggregation_level);
    if (params.summary_only !== undefined) queryParams.append('summary_only', params.summary_only.toString());

    const response = await edgeFunctionAxios.get<SalesReportResponse>(`/sales-report?${queryParams.toString()}`, {
        headers: {
            'X-Organization-ID': userOrgId.toString()
        }
    });

    return response.data;
};